import moment from 'moment'
import React, { useState } from 'react'
import { Link, Route, Switch } from 'react-router-dom'
import { DatePicker, Menu, Dropdown } from 'antd'
import Select from 'react-select'

import { AiOutlineDown, AiOutlineReload } from 'react-icons/ai'

import { stringifyDate, useListData, useSocketEvents, presets } from '../../modules'

import Receive from '../../components/reusable/receive'
import Patient from '../../components/reusable/patient'
import InpatientBadge from '../../components/reusable/inpatientBadge'

import OpticalDispense from '../../components/drawers/opticalDispense'

const { RangePicker } = DatePicker

// Deliberately smaller than Pharmacy/Prescriptions.js - reuses the same generic
// pharmacyRequests/issuePrescription/returnPrescription endpoints (parameterized by
// `group: 'Optical'` instead of forking a Medication-specific copy), but without the
// FEFO batch-override picker sentMedication.js carries - the backend already resolves
// the correct store/batch automatically via resolveDefaultStoreForSaleItem, so this pass
// doesn't need a manual override UI for that.
const Dispensing = () => {
    const [ranges, setRanges] = useState([moment().startOf('day'), moment().endOf('day')])
    const [string, setString] = useState('')
    const [status, setStatus] = useState({ label: 'All', value: 'All' })

    const [
        { loading: qLoading, error: qError, fetch: refresh },
        { list: queue },
        { page = 1, limit = 10, navigate, Paginate }
    ] = useListData('pharmacyRequests', {
        group: 'Optical',
        status: status?.value,
        string,
        from: ranges[0]?._d ?? null,
        to: ranges[1]?._d ?? null,
    }, { notify: true }, 'orders')

    useSocketEvents({
        'new-patient': {
            condition: data => data.destination === 'Optical',
            notif: { message: 'New Patient', type: 'info' },
            callback: refresh
        },
        'new-request': {
            condition: data => data.store === 'Optical',
            notif: { message: 'New Request', type: 'info' },
            callback: refresh
        }
    })

    const search = str => {
        setString(str)
        refresh()
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const changeStatus = stat => {
        setStatus(stat)
        navigate(1)
        refresh()
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div className="sm:flex sm:justify-between sm:items-center mb-8">
                <input
                    onChange={e => search(e.target.value)}
                    className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                    id="grid-search"
                    type="text"
                    placeholder="Patient Name, OP/IP No." />
                <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                    <RangePicker value={ ranges } onChange={selectRange} ranges={ presets } />
                </div>
            </div>
            <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-sm border border-gray-200">
                <header className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="font-semibold text-gray-800 mb-1">Patients' Queue</h2>
                    <span className="flex items-center justify-between">
                        <Select
                            value={status}
                            onChange={changeStatus}
                            styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                            options={ ['All', 'Seen', 'Pending', 'Paid'].map(stat => ({ label: stat, value: stat }))}
                            placeholder="Select Status" />
                        <button onClick={refresh} className="btn ml-2 py-2.5 hisani-btn-green-light text-white">
                            <AiOutlineReload style={{ color: "white" }} />
                        </button>
                    </span>
                </header>
                <div className="p-3">
                    <Receive
                        progress
                        loading={qLoading}
                        error={qError}>
                        {
                            queue?.length > 0 ?
                                <div className="overflow-x-auto">
                                    <table className="table-auto w-full">
                                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                            <tr>
                                                <th className="p-2"><div className="font-semibold text-center">No</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Patient Name</div></th>
                                                <th className="p-2"><div className="font-semibold text-left">Items</div></th>
                                                <th className="p-2"><div className="font-semibold text-center">Sent On</div></th>
                                                <th className="p-2"><div className="font-semibold text-center"></div></th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                                            {
                                                queue.map((order, key) =>
                                                    <tr key={ order._id }>
                                                        <td className="p-2"><div className="text-center">{((page - 1) * limit) + (key + 1)}.</div></td>
                                                        <td className="p-2"><Patient patient={ order?.patient } /></td>
                                                        <td className="p-2"><div className="text-left">{ order?.items?.length ?? 0 }</div></td>
                                                        <td className="p-2"><div className="text-center">{stringifyDate(order.date)}</div></td>
                                                        <td className="p-2 text-center">
                                                            <InpatientBadge show={ order.inPatient }>
                                                                <Dropdown
                                                                    overlay={
                                                                        <Menu>
                                                                            <Link to={ `/app/optical/dispensing/${order._id}` }><Menu.Item>View Request</Menu.Item></Link>
                                                                        </Menu>
                                                                    }>
                                                                    <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                                </Dropdown>
                                                            </InpatientBadge>
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                :
                                <div className="w-full h-16 flex items-center justify-center">
                                    <p className="font-semibold text-base">No patients { string.trim().length > 2 ? `matching "${string}"` : "" } were found</p>
                                </div>
                        }
                    </Receive>
                    {Paginate}
                </div>
            </div>
            <Switch>
                <Route path="/app/optical/dispensing/:id">
                    <OpticalDispense source="/app/optical/dispensing" onPerform={ refresh } />
                </Route>
            </Switch>
        </div>
    )
}

export default Dispensing
